/* PrintStation.jsx — keep this tab open on the store PC.
 *
 * Polls /api/print-jobs/pending every ~5s. For each pending job:
 *   1. POST /api/print-jobs/{id}/claim   (atomic — only one tab wins)
 *   2. Render job.html into a hidden iframe
 *   3. Trigger iframe.contentWindow.print()
 *   4. POST /api/print-jobs/{id}/mark-printed
 *
 * If the user is on a different network, no problem — the queue persists in
 * MongoDB until the store tab comes online.
 */
import { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Printer, Pause, Play, RefreshCw, CheckCircle2, XCircle, Hourglass, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_INTERVAL_MS = 5000;
const PRINT_TIMEOUT_MS = 30000;  // safety net if afterprint never fires

const formatTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleTimeString("en-ZA", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
  } catch { return iso; }
};

const statusBadge = (status) => {
  const map = {
    pending: { cls: "bg-amber-100 text-amber-800", label: "Pending" },
    claimed: { cls: "bg-blue-100 text-blue-800", label: "Printing…" },
    printed: { cls: "bg-green-100 text-green-800", label: "Printed" },
    failed: { cls: "bg-red-100 text-red-800", label: "Failed" },
    cancelled: { cls: "bg-gray-200 text-gray-700", label: "Cancelled" },
  };
  const m = map[status] || { cls: "bg-gray-100 text-gray-700", label: status };
  return <Badge className={`${m.cls} hover:${m.cls}`} data-testid={`status-${status}`}>{m.label}</Badge>;
};

export default function PrintStation() {
  const [paused, setPaused] = useState(false);
  const [busy, setBusy] = useState(false);
  const [recent, setRecent] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [lastPoll, setLastPoll] = useState(null);
  const [station, setStation] = useState(() => localStorage.getItem("print_station_name") || "Store Printer");
  const iframeRef = useRef(null);
  const processingRef = useRef(false);

  const fetchRecent = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/print-jobs/recent?limit=20`);
      setRecent(data || []);
      const pending = (data || []).filter(j => j.status === "pending" || j.status === "claimed").length;
      setPendingCount(pending);
    } catch (e) {
      console.error("Failed to fetch recent jobs:", e);
    }
  }, []);

  const printOneJob = useCallback(async (job) => {
    return new Promise((resolve) => {
      const iframe = iframeRef.current;
      if (!iframe) return resolve({ ok: false, error: "No iframe" });

      let finalized = false;
      const finalize = (ok, error) => {
        if (finalized) return;
        finalized = true;
        clearTimeout(safetyTimer);
        try { iframe.removeEventListener("afterprint", onAfterPrint); } catch (_) { /* ignore */ }
        try { iframe.contentWindow?.removeEventListener("afterprint", onAfterPrint); } catch (_) { /* ignore */ }
        resolve({ ok, error });
      };
      const onAfterPrint = () => finalize(true, null);
      const safetyTimer = setTimeout(() => finalize(true, null), PRINT_TIMEOUT_MS);

      // Write the HTML doc into the iframe's document.
      const doc = iframe.contentDocument || iframe.contentWindow.document;
      doc.open();
      doc.write(job.html || "<html><body><p>(empty)</p></body></html>");
      doc.close();

      // Hook into the iframe's window for afterprint.
      try {
        iframe.contentWindow.addEventListener("afterprint", onAfterPrint);
      } catch (_) { /* ignore */ }

      // Give the browser a tick to paint, then trigger print.
      setTimeout(() => {
        try {
          iframe.contentWindow.focus();
          iframe.contentWindow.print();
        } catch (e) {
          finalize(false, e?.message || "print() threw");
        }
      }, 250);
    });
  }, []);

  const processQueue = useCallback(async () => {
    if (paused || processingRef.current) return;
    processingRef.current = true;
    setBusy(true);
    try {
      const { data: pending } = await axios.get(`${API}/print-jobs/pending`);
      for (const job of pending || []) {
        // Try to claim — atomic so a second tab racing us won't double print.
        let claimed = null;
        try {
          const res = await axios.post(`${API}/print-jobs/${job.id}/claim`);
          claimed = res.data;
        } catch (e) {
          if (e?.response?.status === 409) continue;  // someone else got it
          console.error("Claim failed:", e);
          continue;
        }
        const result = await printOneJob(claimed);
        try {
          if (result.ok) {
            await axios.post(`${API}/print-jobs/${claimed.id}/mark-printed`);
            toast.success(`Printed: ${claimed.title}`);
          } else {
            await axios.post(`${API}/print-jobs/${claimed.id}/mark-failed`, null, {
              params: { error: result.error || "Print error" },
            });
            toast.error(`Print failed: ${claimed.title}`);
          }
        } catch (e) {
          console.error("Failed to finalise job:", e);
        }
      }
    } catch (e) {
      console.error("Poll error:", e);
    } finally {
      setLastPoll(new Date().toISOString());
      await fetchRecent();
      processingRef.current = false;
      setBusy(false);
    }
  }, [paused, printOneJob, fetchRecent]);

  // Polling loop.
  useEffect(() => {
    fetchRecent();
    const id = setInterval(() => { processQueue(); }, POLL_INTERVAL_MS);
    // First tick immediately.
    processQueue();
    return () => clearInterval(id);
  }, [processQueue, fetchRecent]);

  const handleTestPrint = async () => {
    try {
      const html = `<!doctype html><html><head><title>Test</title>
<style>@page { size: A4; margin: 20mm; } body { font-family: Arial; }</style></head>
<body><h1>BH Print Station Test</h1><p>Generated ${new Date().toLocaleString("en-ZA")}</p>
<p>If you can see this on paper, remote printing is working.</p></body></html>`;
      await axios.post(`${API}/print-jobs`, {
        job_type: "html",
        title: `Test print — ${station}`,
        html,
        created_by: station,
      });
      toast.success("Test job queued. Should print in ~5s.");
      fetchRecent();
    } catch (e) {
      toast.error("Failed to queue test print");
    }
  };

  const handleRetry = async (jobId) => {
    try {
      await axios.post(`${API}/print-jobs/${jobId}/retry`);
      toast.success("Re-queued");
      fetchRecent();
    } catch (e) {
      toast.error("Failed to retry");
    }
  };

  const handleCancel = async (jobId) => {
    try {
      await axios.delete(`${API}/print-jobs/${jobId}`);
      fetchRecent();
    } catch (e) {
      toast.error("Failed to cancel");
    }
  };

  const saveStation = () => {
    localStorage.setItem("print_station_name", station);
    toast.success(`Station name saved: ${station}`);
  };

  return (
    <div className="p-6 w-full" data-testid="print-station-page">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`h-3 w-3 rounded-full ${paused ? "bg-gray-400" : "bg-green-500 animate-pulse"}`} />
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Printer className="h-7 w-7" /> Print Station
          </h1>
          <Badge variant="outline" className="ml-2">
            {pendingCount} pending
          </Badge>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={station}
            onChange={(e) => setStation(e.target.value)}
            onBlur={saveStation}
            className="h-9 w-44 rounded border px-2 text-sm"
            placeholder="Station name"
            data-testid="station-name-input"
          />
          <Button variant="outline" onClick={handleTestPrint} data-testid="test-print-btn">
            <Printer className="h-4 w-4 mr-1" /> Test Print
          </Button>
          <Button
            variant={paused ? "default" : "outline"}
            onClick={() => setPaused(p => !p)}
            data-testid="pause-toggle-btn"
          >
            {paused ? <><Play className="h-4 w-4 mr-1" /> Resume</> : <><Pause className="h-4 w-4 mr-1" /> Pause</>}
          </Button>
          <Button variant="outline" onClick={processQueue} disabled={busy} data-testid="refresh-now-btn">
            <RefreshCw className={`h-4 w-4 mr-1 ${busy ? "animate-spin" : ""}`} /> Now
          </Button>
        </div>
      </div>

      <Card className="mb-4 bg-amber-50 border-amber-300">
        <CardContent className="py-3 text-sm text-amber-900 flex items-center gap-3">
          <AlertTriangle className="h-5 w-5 flex-shrink-0" />
          <div>
            <span className="font-semibold">Leave this tab open.</span> It polls the queue every {POLL_INTERVAL_MS / 1000}s and auto-prints incoming jobs to whichever printer this browser is configured to use.
            Last poll: <span className="font-mono">{formatTime(lastPoll)}</span>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr className="text-left">
                  <th className="px-4 py-2 w-[140px]">Status</th>
                  <th className="px-4 py-2">Title</th>
                  <th className="px-4 py-2 w-[120px]">Created</th>
                  <th className="px-4 py-2 w-[120px]">Printed</th>
                  <th className="px-4 py-2 w-[120px]">From</th>
                  <th className="px-4 py-2 w-[180px] text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {recent.length === 0 ? (
                  <tr><td colSpan={6} className="text-center py-12 text-gray-500">
                    <Hourglass className="h-10 w-10 mx-auto mb-2 opacity-40" />
                    Queue is empty. Send a print from any device to test.
                  </td></tr>
                ) : recent.map(job => (
                  <tr key={job.id} className="border-b last:border-0 hover:bg-gray-50" data-testid={`job-row-${job.id}`}>
                    <td className="px-4 py-2">{statusBadge(job.status)}</td>
                    <td className="px-4 py-2 font-medium">
                      {job.title}
                      {job.error && <div className="text-xs text-red-600 mt-0.5">{job.error}</div>}
                    </td>
                    <td className="px-4 py-2 text-gray-600 font-mono text-xs">{formatTime(job.created_at)}</td>
                    <td className="px-4 py-2 text-gray-600 font-mono text-xs">{formatTime(job.printed_at)}</td>
                    <td className="px-4 py-2 text-gray-600 text-xs">{job.created_by || "—"}</td>
                    <td className="px-4 py-2 text-right">
                      {(job.status === "failed" || job.status === "printed") && (
                        <Button size="sm" variant="outline" onClick={() => handleRetry(job.id)} data-testid={`retry-${job.id}`}>
                          Re-print
                        </Button>
                      )}
                      {(job.status === "pending" || job.status === "claimed") && (
                        <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleCancel(job.id)} data-testid={`cancel-${job.id}`}>
                          <XCircle className="h-4 w-4" />
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Hidden iframe — where each job is actually rendered + printed. */}
      <iframe
        ref={iframeRef}
        title="Print Frame"
        style={{
          position: "fixed",
          right: 0,
          bottom: 0,
          width: "1px",
          height: "1px",
          border: "0",
          opacity: 0,
          pointerEvents: "none",
        }}
      />
    </div>
  );
}
